<template>
  <div class="toast-container">
    <TransitionGroup name="toast">
      <div
        v-for="toast in visibleToasts"
        :key="toast.id"
        class="toast"
        :class="toast.type"
        @mouseenter="setFrozen(true)"
        @mouseleave="setFrozen(false)"
      >
        <span class="toast-icon">{{ getIcon(toast.type) }}</span>
        <span class="toast-message">{{ toast.message }}</span>
        <UiIconButton
          v-if="toast.sticky"
          class="toast-close"
          variant="ghost"
          size="sm"
          :aria-label="`Dismiss toast`"
          @click.stop="removeToast(toast.id)"
        >
          {{ ICONS.actions.close }}
        </UiIconButton>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useToast, type Toast } from "../composables/useToast";
import { ICONS } from "../lib/icons";
import UiIconButton from "./ui/UiIconButton.vue";

const { visibleToasts, removeToast, setFrozen, setMaxVisible } = useToast();

const viewportH = ref<number>(typeof window === "undefined" ? 800 : window.innerHeight);
const releaseFreeze = () => setFrozen(false);

function onResize() {
  viewportH.value = window.innerHeight;
}

onMounted(() => {
  window.addEventListener("resize", onResize);
  window.addEventListener("blur", releaseFreeze);
  window.addEventListener("focus", releaseFreeze);
  window.addEventListener("pointercancel", releaseFreeze);
  document.addEventListener("visibilitychange", releaseFreeze);
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
  window.removeEventListener("blur", releaseFreeze);
  window.removeEventListener("focus", releaseFreeze);
  window.removeEventListener("pointercancel", releaseFreeze);
  document.removeEventListener("visibilitychange", releaseFreeze);
});

const maxVisible = computed(() => {
  const h = Math.max(320, viewportH.value);
  const approxToast = 84;
  const reserved = h < 900 ? 220 : 260;
  return Math.max(2, Math.min(7, Math.floor((h - reserved) / approxToast)));
});

watch(
  () => maxVisible.value,
  (next) => setMaxVisible(next),
  { immediate: true }
);
watch(
  () => visibleToasts.value.length,
  (next) => {
    if (next === 0) setFrozen(false);
  }
);

function getIcon(type: Toast["type"]): string {
  switch (type) {
    case "item":
      return ICONS.toasts.item;
    case "xp":
      return ICONS.toasts.xp;
    case "info":
      return ICONS.toasts.info;
    case "success":
      return ICONS.toasts.success;
    case "warning":
      return ICONS.toasts.warning;
    case "error":
      return ICONS.toasts.error;
    case "combat-good":
      return ICONS.toasts.combatGood;
    case "combat-bad":
      return ICONS.toasts.combatBad;
    default:
      return ICONS.toasts.default;
  }
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  bottom: calc(1.5rem + var(--toast-dock-offset, 0px));
  left: calc(var(--shell-rail, 220px) + 1.5rem);
  z-index: 9999;
  display: flex;
  flex-direction: column-reverse;
  gap: 0.5rem;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: var(--radius-sm);
  background: var(--toast-bg);
  border: 1px solid var(--border-1);
  box-shadow: var(--shadow-md);
  color: var(--color-text);
  font-size: 0.9rem;
  min-width: 220px;
  max-width: 320px;
  pointer-events: auto;
}

.toast.item {
  border-left: 3px solid var(--color-success);
  background: linear-gradient(90deg, color-mix(in srgb, var(--state-active) 16%, transparent) 0%, var(--toast-bg) 22%);
}

.toast.xp {
  border-left: 3px solid var(--color-warning);
  background: linear-gradient(90deg, color-mix(in srgb, var(--state-warning) 16%, transparent) 0%, var(--toast-bg) 22%);
}

.toast.info {
  border-left: 3px solid var(--color-info);
}

.toast.success {
  border-left: 3px solid var(--color-success);
  background: linear-gradient(90deg, color-mix(in srgb, var(--state-active) 16%, transparent) 0%, var(--toast-bg) 22%);
}

.toast.warning {
  border-left: 3px solid var(--color-warning);
}

.toast.error {
  border-left: 3px solid var(--color-danger);
}

.toast.combat-good {
  border-left: 3px solid var(--color-success);
  background: linear-gradient(90deg, color-mix(in srgb, var(--state-active) 20%, transparent) 0%, var(--toast-bg) 22%);
}

.toast.combat-bad {
  border-left: 3px solid var(--color-danger);
  background: linear-gradient(90deg, color-mix(in srgb, var(--state-danger) 20%, transparent) 0%, var(--toast-bg) 22%);
}

.toast-icon {
  font-size: 1.1rem;
}

.toast-message {
  flex: 1;
}

.toast-close {
  flex: 0 0 auto;
  margin-left: 0.25rem;
}

/* Transition animations */
.toast-enter-active {
  animation: slideIn 0.3s ease-out;
}

.toast-leave-active {
  animation: slideOut 0.3s ease-in;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-40%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideOut {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-40%);
  }
}

@media (max-width: 900px) {
  .toast-container {
    top: calc(env(safe-area-inset-top) + 1rem);
    bottom: auto;
    left: 50%;
    transform: translateX(-50%);
  }

  .toast-enter-active {
    animation: slideDown 0.3s ease-out;
  }

  .toast-leave-active {
    animation: slideUp 0.3s ease-in;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-40%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-40%);
    }
  }
}
</style>
